import React, { useEffect, useRef, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import PickerPopover from "./PickerPopover";
import { toDateInput } from "../utils/calendar";

/**
 * Selector de fecha propio, a juego con el de hora.
 *
 * Trabaja siempre con "YYYY-MM-DD", igual que <input type="date">, así que es
 * intercambiable con él. Acepta min y max en el mismo formato.
 */

const WEEKDAYS = ["L", "M", "X", "J", "V", "S", "D"];

/** "YYYY-MM-DD" → Date local, sin pasar por UTC (que desplazaría el día). */
const parse = (value) => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Lunes = 0, domingo = 6 (en España la semana empieza en lunes). */
const mondayFirst = (date) => (date.getDay() + 6) % 7;

/** Rejilla del mes, rellenada con los huecos de la primera semana. */
const buildGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const days = new Date(year, month + 1, 0).getDate();
  const blanks = mondayFirst(first);
  return [
    ...Array.from({ length: blanks }, () => null),
    ...Array.from({ length: days }, (_, i) => new Date(year, month, i + 1)),
  ];
};

function DatePicker({ value = "", onChange, min, max, id }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const selected = parse(value);
  const minDate = parse(min);
  const maxDate = parse(max);

  const [cursor, setCursor] = useState(() => selected || new Date());

  // Al abrir, situarse en el mes de la fecha elegida
  useEffect(() => {
    if (open && selected) setCursor(new Date(selected.getFullYear(), selected.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const today = new Date();
  const sameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const disabled = (d) => (minDate && d < minDate) || (maxDate && d > maxDate);

  const shiftMonth = (step) =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + step, 1));

  const monthLabel = cursor.toLocaleDateString("es-ES", { month: "long", year: "numeric" });

  const pick = (d) => {
    onChange(toDateInput(d));
    setOpen(false); // una fecha es una sola elección: se cierra al momento
  };

  const label = selected
    ? selected.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })
    : "";

  return (
    <>
      <button
        type="button"
        id={id}
        ref={anchorRef}
        onClick={() => setOpen((v) => !v)}
        className={`dyd-picker-field ${open ? "is-open" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Calendar size={15} />
        <span className={label ? "" : "dyd-picker-placeholder"}>{label || "Elegir fecha"}</span>
      </button>

      <PickerPopover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={274}
      >
        <div role="dialog" aria-label="Elegir fecha">
          <div className="dyd-dp-head">
            <button type="button" className="dyd-dp-nav" onClick={() => shiftMonth(-1)} aria-label="Mes anterior">
              <ChevronLeft size={16} />
            </button>
            <span className="dyd-dp-month">{monthLabel}</span>
            <button type="button" className="dyd-dp-nav" onClick={() => shiftMonth(1)} aria-label="Mes siguiente">
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="dyd-dp-weekdays" aria-hidden="true">
            {WEEKDAYS.map((d, i) => <span key={i}>{d}</span>)}
          </div>

          <div className="dyd-dp-grid">
            {buildGrid(cursor.getFullYear(), cursor.getMonth()).map((d, i) =>
              d === null ? (
                <span key={`hueco-${i}`} />
              ) : (
                <button
                  key={d.getDate()}
                  type="button"
                  disabled={disabled(d)}
                  aria-current={sameDay(d, today) ? "date" : undefined}
                  aria-pressed={sameDay(d, selected)}
                  className={[
                    "dyd-dp-day",
                    sameDay(d, selected) ? "is-selected" : "",
                    sameDay(d, today) ? "is-today" : "",
                  ].join(" ").trim()}
                  onClick={() => pick(d)}
                >
                  {d.getDate()}
                </button>
              )
            )}
          </div>
        </div>
      </PickerPopover>
    </>
  );
}

export default DatePicker;
